/**
 * 上传 API
 */

import SparkMD5 from 'spark-md5'
import { UploadSpeedTracker, type UploadStats } from '@/utils/upload-speed-tracker'
import { calculateOptimalChunkSize } from '@/utils/chunk-size-strategy'

export type { UploadStats } from '@/utils/upload-speed-tracker'
export type { ChunkSizeConfig } from '@/utils/chunk-size-strategy'

export interface UploadMetadata {
  encrypted?: boolean          // 加密传输
  allowDownload?: boolean      // 允许下载
  allowClone?: boolean         // 允许克隆
  allowTransfer?: boolean      // 允许转存
  description?: string         // 补充描述
}

export interface InitUploadParams {
  fileName: string
  fileSize: number
  fileMD5: string
  chunkSize: number
  totalChunks: number
  shareId?: number            // 多文件上传时，后续文件传入第一个文件的 shareId
  deviceId?: string           // 设备唯一标识
  expireDays?: number         // 有效期（天）- 仅第一个文件需要
  password?: string           // 分享密码 - 仅第一个文件需要
  burnAfterRead?: boolean     // 下载后销毁 - 仅第一个文件需要
  maxDownloads?: number       // 最大下载次数 - 仅第一个文件需要
  metadata?: UploadMetadata   // 额外参数 - 仅第一个文件需要
}

export interface UploadResponse {
  code: number
  data: {
    uploadId?: string
    uploadedChunks: number[]
    exists: boolean
    resumed?: boolean
    fileId?: string
    shareId?: number
    shareKey?: string
    downloadUrl?: string
    expireAt?: string
  }
}

export interface CompleteUploadResponse {
  code: number
  data: {
    fileId: string
    shareId?: number
    shareKey?: string
    downloadUrl: string
    burnAfterRead?: boolean
    expireDays?: number
    expireAt?: string
    uploaded?: boolean
    metadata?: UploadMetadata
  }
}

export type SimpleUploadResponse = CompleteUploadResponse

export async function initUpload(params: InitUploadParams): Promise<UploadResponse> {
  const token = localStorage.getItem('accessToken')
  const headers: any = {
    'Content-Type': 'application/json',
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  
  const response = await fetch('/api/upload/init', {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Upload initialization failed')
  }
  
  return response.json()
}

export async function uploadChunk(
  uploadId: string,
  chunkIndex: number,
  chunkData: Blob,
  onProgress?: (loaded: number, total: number) => void
): Promise<any> {
  const arrayBuffer = await chunkData.arrayBuffer()
  const spark = new SparkMD5.ArrayBuffer()
  spark.append(arrayBuffer)
  const chunkMD5 = spark.end()
  
  const formData = new FormData()
  formData.append('upload_id', uploadId)
  formData.append('chunk_index', chunkIndex.toString())
  formData.append('chunk_md5', chunkMD5)
  formData.append('chunk_data', chunkData)
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded, event.total)
      }
    }
    
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText)
          resolve(response)
        } catch (error) {
          reject(new Error('解析响应失败'))
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText)
          reject(new Error(error.message || `Chunk ${chunkIndex} upload failed`))
        } catch {
          reject(new Error(`Chunk ${chunkIndex} upload failed: ${xhr.statusText}`))
        }
      }
    }
    
    xhr.onerror = () => reject(new Error(`Chunk ${chunkIndex} network error`))
    xhr.open('POST', '/api/upload/chunk')
    
    const token = localStorage.getItem('accessToken')
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }
    
    xhr.send(formData)
  })
}

export async function completeUpload(uploadId: string, shareId?: number): Promise<CompleteUploadResponse> {
  const token = localStorage.getItem('accessToken')
  const headers: any = {
    'Content-Type': 'application/json',
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  
  const response = await fetch('/api/upload/complete', {
    method: 'POST',
    headers,
    body: JSON.stringify({ 
      uploadId: uploadId,
      shareId: shareId
    }),
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Upload completion failed')
  }
  
  return response.json()
}

export async function calculateFileMD5(
  file: File,
  onProgress?: (chunksDone: number, totalChunks: number) => void
): Promise<string> {
  return new Promise((resolve, reject) => {
    const blobSlice = File.prototype.slice
    const chunkSize = 2097152 // 2MB per chunk
    const chunks = Math.ceil(file.size / chunkSize)
    let currentChunk = 0
    const spark = new SparkMD5.ArrayBuffer()
    const fileReader = new FileReader()

    fileReader.onload = (e) => {
      spark.append(e.target?.result as ArrayBuffer)
      currentChunk++

      if (onProgress) {
        onProgress(currentChunk, chunks)
      }

      if (currentChunk < chunks) {
        loadNext()
      } else {
        resolve(spark.end())
      }
    }

    fileReader.onerror = () => {
      reject(new Error('MD5 calculation failed'))
    }

    function loadNext() {
      const start = currentChunk * chunkSize
      const end = Math.min(start + chunkSize, file.size)
      fileReader.readAsArrayBuffer(blobSlice.call(file, start, end))
    }

    loadNext()
  })
}

export async function uploadFileWithMetadata(
  file: File,
  options: {
    shareId?: number
    deviceId?: string
    expireDays?: number
    password?: string
    burnAfterRead?: boolean
    maxDownloads?: number
    encrypted?: boolean
    allowDownload?: boolean
    allowClone?: boolean
    allowTransfer?: boolean
    description?: string
    onProgress?: (progress: number) => void
    onSpeedUpdate?: (stats: UploadStats) => void
    onShareInfoReady?: (shareKey: string, downloadUrl: string) => void
    onPhaseChange?: (phase: 'hashing' | 'uploading' | 'completing') => void
  }
): Promise<CompleteUploadResponse> {
  return uploadFileChunked(file, options)
}

async function uploadFileChunked(
  file: File,
  options: {
    shareId?: number
    deviceId?: string
    expireDays?: number
    password?: string
    burnAfterRead?: boolean
    maxDownloads?: number
    encrypted?: boolean
    allowDownload?: boolean
    allowClone?: boolean
    allowTransfer?: boolean
    description?: string
    onProgress?: (progress: number) => void
    onSpeedUpdate?: (stats: UploadStats) => void
    onShareInfoReady?: (shareKey: string, downloadUrl: string) => void
    onPhaseChange?: (phase: 'hashing' | 'uploading' | 'completing') => void
    forceChunkSize?: number
  }
): Promise<CompleteUploadResponse> {
  const chunkConfig = calculateOptimalChunkSize(file.size)
  const chunkSize = options.forceChunkSize || chunkConfig.chunkSize
  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize))
  
  try {
    if (options.onPhaseChange) options.onPhaseChange('hashing')
    if (options.onProgress) options.onProgress(0)
    const fileMD5 = await calculateFileMD5(file, (done, total) => {
      if (options.onProgress) {
        options.onProgress(Math.round((done / total) * 10))
      }
    })
    
    if (options.onProgress) options.onProgress(10)
    const initResponse = await initUpload({
      fileName: file.name,
      fileSize: file.size,
      fileMD5,
      chunkSize,
      totalChunks,
      shareId: options.shareId,
      deviceId: options.deviceId,
      expireDays: options.expireDays,
      password: options.password,
      burnAfterRead: options.burnAfterRead,
      maxDownloads: options.maxDownloads,
      metadata: {
        encrypted: options.encrypted,
        allowDownload: options.allowDownload,
        allowClone: options.allowClone,
        allowTransfer: options.allowTransfer,
        description: options.description,
      },
    })
    
    if (options.onShareInfoReady && initResponse.data.shareKey) {
      options.onShareInfoReady(
        initResponse.data.shareKey,
        initResponse.data.downloadUrl || ''
      )
    }
    
    if (initResponse.data.exists === true) {
      if (options.onProgress) options.onProgress(100)
      return {
        code: 200,
        data: {
          fileId: initResponse.data.fileId || '',
          shareId: initResponse.data.shareId,
          shareKey: initResponse.data.shareKey,
          downloadUrl: initResponse.data.downloadUrl || '',
          burnAfterRead: options.burnAfterRead || false,
          expireDays: options.expireDays || 7,
          expireAt: initResponse.data.expireAt || '',
          metadata: {
            encrypted: options.encrypted,
            allowDownload: options.allowDownload,
            allowClone: options.allowClone,
            allowTransfer: options.allowTransfer,
            description: options.description,
          }
        }
      }
    }
    
    const uploadId = initResponse.data.uploadId
    if (!uploadId) {
      throw new Error('初始化失败：未获取到uploadId')
    }
    
    const uploadedChunks = initResponse.data.uploadedChunks || []
    const isResumed = initResponse.data.resumed === true
    
    if (isResumed && uploadedChunks.length > 0) {
      const resumedProgress = 10 + (uploadedChunks.length / totalChunks) * 80
      if (options.onProgress) options.onProgress(Math.round(resumedProgress))
    }
    
    const speedTracker = new UploadSpeedTracker()
    let totalUploaded = uploadedChunks.length * chunkSize
    
    if (options.onPhaseChange) options.onPhaseChange('uploading')
    for (let i = 0; i < totalChunks; i++) {
      if (uploadedChunks.includes(i)) {
        continue
      }
      
      const start = i * chunkSize
      const end = Math.min(start + chunkSize, file.size)
      const chunk = file.slice(start, end)
      const chunkStart = start
      
      await uploadChunk(uploadId, i, chunk, (chunkLoaded) => {
        totalUploaded = chunkStart + chunkLoaded
        if (options.onProgress) {
          const progress = 10 + (totalUploaded / file.size) * 80
          options.onProgress(Math.round(progress))
        }
        if (options.onSpeedUpdate) {
          const stats = speedTracker.update(totalUploaded, file.size)
          options.onSpeedUpdate(stats)
        }
      })
      
      totalUploaded = end
    }
    
    if (options.onPhaseChange) options.onPhaseChange('completing')
    if (options.onProgress) options.onProgress(95)
    const shareId = initResponse.data.shareId
    const shareIdNum = shareId != null 
      ? (typeof shareId === 'number' ? shareId : Number(shareId))
      : undefined
    const result = await completeUpload(uploadId, shareIdNum)
    
    if (options.onProgress) options.onProgress(100)
    return result
  } catch (error) {
    console.error('Chunked upload failed:', error)
    throw error
  }
}
